// Defect labels mapping
const DEFECT_LABELS = {
  0: 'cracking',
  1: 'damaged_footpath', 
  2: 'damaged_kerb_faded_paint',
  3: 'damaged_missing_cover_slabs',
  4: 'deteriorated_damaged_plants',
  5: 'edge_drop_uneven_shoulders',
  6: 'missing_plants_irregular_gaps',
  7: 'potholes',
  8: 'rain_cuts_shoulders',
  9: 'reduced_visibility_due_to_plant_growth',
  10: 'rutting',
  11: 'vegetation_growth_shoulders',
  12: 'water_stagnation'
};

// Feature labels mapping
const FEATURE_LABELS = {
  0: 'crash_barriers_guard_rails_damaged',
  1: 'damaged_blinkers_attenuators_delineators_anti_glare',
  2: 'damaged_bus_shelters_faded_markings_damaged_signages', 
  3: 'damaged_lights',
  4: 'damaged_road_studs_rumble_strips_hazard_markers',
  5: 'damaged_sign_boards_structures',
  6: 'encroachments_illegal_parking',
  7: 'faded_painting_barrier_guard_rail',
  8: 'faded_pavement_marking',
  9: 'inappropriate_diversions',
  10: 'missing_assets_change_detection',
  11: 'missing_poor_safety_measures',
  12: 'non_functional_lights',
  13: 'overall_cleanliness',
  14: 'pavement_marking_visibility_day_night',
  15: 'road_stud_rumble_strip_visibility_day_night',
  16: 'signage_visibility_day_night',
  17: 'unauthorized_median_openings',
  18: 'unauthorized_signboards_hoardings'
};

/**
 * Maps a defect label from numeric to descriptive text
 * @param {number|string} label - The numeric label
 * @returns {string} The descriptive defect label
 */
export const mapDefectLabel = (label) => {
  const numericLabel = typeof label === 'string' ? parseInt(label, 10) : label;
  return DEFECT_LABELS[numericLabel] || label;
};

/**
 * Maps a feature label from numeric to descriptive text
 * @param {number|string} label - The numeric label
 * @returns {string} The descriptive feature label
 */
export const mapFeatureLabel = (label) => {
  const numericLabel = typeof label === 'string' ? parseInt(label, 10) : label;
  return FEATURE_LABELS[numericLabel] || label;
};

/**
 * Maps any label (attempts both defect and feature mapping)
 * @param {number|string} label - The numeric label
 * @returns {string} The descriptive label
 */
export const mapLabel = (label) => {
  const numericLabel = typeof label === 'string' ? parseInt(label, 10) : label;
  return DEFECT_LABELS[numericLabel] || FEATURE_LABELS[numericLabel] || label;
};

// ─── Universal severity / color logic ────────────────────────────────────────
// Single source of truth for label → severity → color for ALL organizations
// EXCEPT those with their own custom logic (Kota, Bhilwara). Only labels
// listed in SEVERITY_MAP are treated as defects; anything else resolves to
// severity 'none' (Green).

export const SEVERITY_COLORS = {
  high:   '#DC3C3C',
  medium: '#E87A00',
  low:    '#C8C800',
  none:   '#00BB00',
};

// Canonical label (normalized lowercase) → 'high' | 'medium' | 'low'
export const SEVERITY_MAP = {
  // ─── Linear / Interval ──────────────────────────────────────────────
  'rutting':                                            'high',
  'shoulder - rain cuts':                               'medium',
  'shoulder - edge drop':                               'medium',
  'shoulder - unevenness':                              'medium',
  'shoulder - vegetation growth':                       'low',
  'damaged kerb':                                       'high',
  'faded kerb painting':                                'medium',
  'reduced visibility due to plantation growth':        'high',
  'damaged crash barriers':                             'high',
  'faded painting concrete crash (cc) barrier':         'medium',
  'damaged (mbcb) metal beam crash barrier':            'high',
  'damaged (pgr) pedestrian guard rail':                'high',
  'barriers - faded painting guard rails':              'medium',
  'faded pavement marking':                             'high',
  'pavement marking - poor visibility (day)':           'medium',
  'pavement marking - poor visibility (night)':         'high',
  'bus bay - faded markings':                           'low',
  'truck lay by - faded markings':                      'low',
  'work zone - poor diversion arrangement / condition': 'high',
  'unauthorized median openings':                       'high',
  // ─── Spontaneous ────────────────────────────────────────────────────
  'potholes':                                           'high',
  'cracking':                                           'medium',
  'missing plants / irregular gaps (median)':           'medium',
  'deteriorated or damaged plants (median)':            'medium',
  'damaged drain cover slabs':                          'high',
  'missing drain cover slabs':                          'high',
  'water stagnation':                                   'medium',
  'damaged footpath tiles / paver blocks':              'medium',
  'missing assets (guard rails)':                       'high',
  'damaged sign boards / sign structures':              'high',
  'signage - poor visibility (day)':                    'medium',
  'signage - poor visibility (night)':                  'high',
  'missing assets (signages)':                          'high',
  'damaged blinkers':                                   'high',
  'damaged attenuators':                                'high',
  'damaged delineators':                                'high',
  'damaged anti-glare':                                 'high',
  'damaged road studs':                                 'high',
  'road studs - poor visibility (day)':                 'medium',
  'road studs - poor visibility (night)':               'high',
  'damaged rumble strips':                              'high',
  'damaged hazard markers':                             'high',
  'bus bay - damaged shelters':                         'medium',
  'bus bay - damaged signages':                         'low',
  'truck lay by - damaged shelters':                    'medium',
  'truck lay by - damaged signages':                    'low',
  'damaged highway lights':                             'high',
  'non-functional highway lights':                      'high',
  'missing assets (street lights)':                     'high',
  'work zone - inadequate signboard visibility':        'medium',
  'work zone - inadequate barricading':                 'high',
  'unauthorized signboards':                            'low',
  'unauthorized hoardings':                             'medium',
  'illegal parking':                                    'low',
  'general encroachments':                              'medium',
  'cleanliness - litter':                               'low',
  'cleanliness - debris':                               'low',
};

// Backend / numeric-mapped label variants → SEVERITY_MAP key.
// Backend sends labels in snake_case (e.g. "rain_cuts_shoulders"); SEVERITY_MAP
// uses the human-readable canonical form ("shoulder - rain cuts"). This alias
// table lets either form resolve to the same severity.
const _LABEL_ALIASES = {
  'rain cuts shoulders':                           'shoulder - rain cuts',
  'edge drop uneven shoulders':                    'shoulder - edge drop',
  'shoulder edge drop':                            'shoulder - edge drop',
  'shoulder unevenness':                           'shoulder - unevenness',
  'vegetation growth shoulders':                   'shoulder - vegetation growth',
  'shoulder vegetation growth':                    'shoulder - vegetation growth',
  'damaged kerb faded paint':                      'damaged kerb',
  'reduced visibility due to plant growth':        'reduced visibility due to plantation growth',
  'reduced visibility due to overgrowth':          'reduced visibility due to plantation growth',
  'crash barriers guard rails damaged':            'damaged crash barriers',
  'damaged metal beam crash barrier':              'damaged (mbcb) metal beam crash barrier',
  'damaged pedestrian guard rail':                 'damaged (pgr) pedestrian guard rail',
  'faded painting concrete crash barrier':         'faded painting concrete crash (cc) barrier',
  'faded painting barrier guard rail':             'barriers - faded painting guard rails',
  'pavement marking visibility day night':         'pavement marking - poor visibility (day)',
  'pavement marking - poor visibility day':        'pavement marking - poor visibility (day)',
  'pavement marking - poor visibility night':      'pavement marking - poor visibility (night)',
  'work zone poor diversion arrangement condition':'work zone - poor diversion arrangement / condition',
  'inappropriate diversions':                      'work zone - poor diversion arrangement / condition',
  'pothole':                                       'potholes',
  'cracks':                                        'cracking',
  'crack':                                         'cracking',
  'missing plants irregular gaps':                 'missing plants / irregular gaps (median)',
  'deteriorated damaged plants':                   'deteriorated or damaged plants (median)',
  'damaged missing cover slabs':                   'damaged drain cover slabs',
  'damaged footpath':                              'damaged footpath tiles / paver blocks',
  'damaged sign boards structures':                'damaged sign boards / sign structures',
  'damaged sign boards':                           'damaged sign boards / sign structures',
  'signage visibility day night':                  'signage - poor visibility (day)',
  'signage - poor visibility day':                 'signage - poor visibility (day)',
  'signage - poor visibility night':               'signage - poor visibility (night)',
  'missing assets change detection':               'missing assets (signages)',
  'damaged blinkers attenuators delineators anti glare': 'damaged blinkers',
  'damaged road studs rumble strips hazard markers':     'damaged road studs',
  'road stud rumble strip visibility day night':   'road studs - poor visibility (day)',
  'road studs - poor visibility day':              'road studs - poor visibility (day)',
  'road studs - poor visibility night':            'road studs - poor visibility (night)',
  'damaged bus shelters faded markings damaged signages':'bus bay - damaged shelters',
  'damaged bus shelters':                          'bus bay - damaged shelters',
  'truck lay by damaged shelters':                 'truck lay by - damaged shelters',
  'truck lay by damaged signages':                 'truck lay by - damaged signages',
  'bus bay damaged shelters':                      'bus bay - damaged shelters',
  'bus bay damaged signages':                      'bus bay - damaged signages',
  'damaged lights':                                'damaged highway lights',
  'non functional lights':                         'non-functional highway lights',
  'non functional highway lights':                 'non-functional highway lights',
  'missing poor safety measures':                  'work zone - inadequate barricading',
  'work zone inadequate signboard visibility':     'work zone - inadequate signboard visibility',
  'work zone inadequate barricading':              'work zone - inadequate barricading',
  'unauthorized signboards hoardings':             'unauthorized hoardings',
  'encroachments illegal parking':                 'illegal parking',
  'general encroachment':                          'general encroachments',
  'overall cleanliness':                           'cleanliness - litter',
  'cleanliness litter':                            'cleanliness - litter',
  'cleanliness debris':                            'cleanliness - debris',
};

const _normLabel = (label) =>
  (label || '').toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Returns 'high' | 'medium' | 'low' | 'none' for the given label using the
 * universal SEVERITY_MAP. Accepts both human-readable and snake_case backend
 * label forms via the alias table. Labels not in the map resolve to 'none'.
 */
export const getUniversalSeverityForLabel = (rawLabel) => {
  const norm = _normLabel(rawLabel);
  const key = _LABEL_ALIASES[norm] || norm;
  return SEVERITY_MAP[key] || 'none';
};

/**
 * Returns the hex color for a label using the universal SEVERITY_MAP.
 * Returns null for unmapped labels so callers can decide whether to treat
 * them as Green or skip them.
 */
export const getUniversalColorForLabel = (rawLabel) => {
  const sev = getUniversalSeverityForLabel(rawLabel);
  return sev === 'none' ? null : SEVERITY_COLORS[sev];
};